import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, X } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { dayHoursOf } from './attendanceStatus';
import { asClockTime, clinicMinutes, minutesOf, reminderStore, todaySlot } from './reminders';

// A nudge, a quarter of an hour before somebody's day ends, to mark their time out before they
// leave. Forgetting is the single most common way an attendance record goes wrong: the shift is
// closed by the app hours later at the day's end and the real leaving time is lost.
//
// It is NOT a poll. The shift's end and whether they are punched in are read once, and the clock
// runs in the browser from there; a background poll through apiRequest would sign the user out of
// the whole app on any 401, which is exactly the bug that bit the punch-code dialog. The one extra
// read happens at the moment the reminder is about to appear, to be sure they have not already
// punched out while the page sat open.

const LEAD_MINUTES = 15;
const SNOOZE_MINUTES = 10;
const TICK_MS = 30 * 1000;

const STORE = reminderStore('clinic_punchout');

const PunchOutReminder = ({ authUser, onOpenAttendance }) => {
  const userId = authUser?.id;
  const [shift, setShift] = useState(null);   // { endsAt: 'HH:mm', punchedIn: bool }
  const [showing, setShowing] = useState(false);
  const [snoozeLeft, setSnoozeLeft] = useState(true);
  const checking = useRef(false);

  // One read of where they stand, repeated only when they actually punch.
  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const mine = await attendanceAPI.mine();
      const today = todaySlot(dayHoursOf(mine?.schedule));
      setShift(today?.to ? { endsAt: today.to, punchedIn: Boolean(mine?.open) } : null);
    } catch {
      setShift(null);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const again = () => load();
    window.addEventListener('clinic:attendance-changed', again);
    return () => window.removeEventListener('clinic:attendance-changed', again);
  }, [load]);

  useEffect(() => {
    if (!shift || !shift.punchedIn || showing) return undefined;

    const tick = async () => {
      const saved = STORE.read(userId);
      if (saved.state === 'done') return;

      const endsAt = minutesOf(shift.endsAt);
      const now = clinicMinutes();
      // Shown from a quarter of an hour before the end onwards, so somebody who was away from
      // their screen at the moment it was due still gets it when they come back.
      const due = saved.state === 'snoozed'
        ? now >= saved.until
        : now >= endsAt - LEAD_MINUTES;
      if (!due || checking.current) return;

      // They may have punched out on their phone while this page sat open.
      checking.current = true;
      try {
        const mine = await attendanceAPI.mine();
        if (!mine?.open) {
          setShift((s) => (s ? { ...s, punchedIn: false } : s));
          return;
        }
      } catch {
        return;
      } finally {
        checking.current = false;
      }
      setSnoozeLeft(saved.state !== 'snoozed');
      setShowing(true);
    };

    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [shift, showing, userId]);

  if (!showing || !shift) return null;

  const dismiss = () => {
    STORE.write(userId, { state: 'done' });
    setShowing(false);
  };

  const snooze = () => {
    STORE.write(userId, { state: 'snoozed', until: clinicMinutes() + SNOOZE_MINUTES });
    setShowing(false);
  };

  const openIt = () => {
    STORE.write(userId, { state: 'done' });
    setShowing(false);
    if (onOpenAttendance) onOpenAttendance();
  };

  // z-40, one below dialogs: at z-50 this card covered the bottom of an open dialog on a phone,
  // such as the punch window's position line and its buttons (2026-10-21).
  return (
    <div
      role="alertdialog"
      aria-label="Time to mark your time out"
      className="fixed z-40 bottom-4 right-4 left-4 sm:left-auto sm:w-96 bg-white rounded-2xl shadow-2xl border border-amber-300"
    >
      <div className="p-4 flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Clock className="w-5 h-5 text-amber-700" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900">Mark your time out</p>
          <p className="text-sm text-gray-600 mt-0.5">
            Your day ends at {asClockTime(shift.endsAt)}. Mark your time out before you leave, or the
            app closes the shift for you and the real time is lost.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={openIt}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
            >
              Mark my time out
            </button>
            {snoozeLeft && (
              <button
                type="button"
                onClick={snooze}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Remind me in {SNOOZE_MINUTES} minutes
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="p-1.5 h-fit hover:bg-gray-100 rounded-lg flex-shrink-0"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default PunchOutReminder;
